import { useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Icon } from "@/components/ui/Icon";
import {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/AlertDialog";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { MentionTextarea } from "@/components/ui/MentionTextarea";
import {
  useCreatePostMutation,
  useCreatePollMutation,
  useCreatePostMediaMutation,
  useUpdatePostMutation,
  useCheckClubMembershipQuery,
  useFetchBannedWordsQuery,
  useFetchClubEmailQuotaQuery,
  useFetchClubMemberCountQuery,
  useSendPostEmailMutation,
  type Post,
} from "@/api/clubsApi";
import { isOwnerOrCohost } from "@tinyclub/shared/models";
import { validateContent } from "@tinyclub/shared/utils";
import { cn } from "@/components/ui/cn";
import { LocationPickerModal } from "@/components/location";
import { PollEditorModal, type PollData } from "./PollEditorModal";
import { usePublicKeys } from "@/components/providers/PublicKeysProvider";
import { uploadImage, uploadVideo, deleteFiles, type ImageUploadResult, type VideoUploadResult } from "@/utils/uploadUtils";
import { supabase } from "@/lib/supabase";
import type { GeocodingFeature } from "@tinyclub/shared/models";

// Preview height matching mobile PREVIEW_H constant
const PREVIEW_H = 200;

// Pre-upload state types
type MediaUploadResult =
  | { kind: "image"; result: ImageUploadResult }
  | { kind: "video"; result: VideoUploadResult };

type PreUploadState =
  | { status: "idle" }
  | { status: "uploading"; progress: number }
  | { status: "complete"; upload: MediaUploadResult }
  | { status: "error"; error: string };

interface CreatePostFormProps {
  clubId: string;
  authorId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export interface PostFormContentProps {
  clubId: string;
  authorId: string;
  editingPost?: Post | null;
  onClose: () => void;
  onSuccess: () => void;
  /** Render cancel/submit buttons externally (for SidePanel footer) */
  renderFooter?: (props: {
    canSubmit: boolean;
    isSubmitting: boolean;
    onCancel: () => void;
    onSubmit: () => void;
    submitLabel: string;
  }) => React.ReactNode;
}

/**
 * CreatePostForm - Modal for creating new posts in a club.
 * Matches mobile create-edit-post structure and styling.
 * Uses pre-upload pattern: upload starts when image is selected, not on submit.
 */
export function CreatePostForm({
  clubId,
  authorId,
  open,
  onOpenChange,
}: CreatePostFormProps) {
  return (
    <PostFormContent
      clubId={clubId}
      authorId={authorId}
      onClose={() => onOpenChange(false)}
      onSuccess={() => onOpenChange(false)}
      renderFooter={undefined}
    >
      {(formContent, footerProps) => (
        <>
          <Dialog open={open} onOpenChange={() => {
            if (!footerProps.isSubmitting) {
              footerProps.onCancel();
            }
          }}>
            <DialogContent className="max-w-[620px]" onInteractOutside={(e) => e.preventDefault()}>
              <form onSubmit={(e) => { e.preventDefault(); footerProps.onSubmit(); }}>
                <DialogHeader>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={footerProps.onCancel}
                      className="flex items-center justify-center size-10 rounded-full bg-surface-1 text-text-secondary"
                    >
                      <Icon name="xmark" size={17} />
                    </button>
                    <DialogTitle className="flex-1 text-center text-h6">New post</DialogTitle>
                    {/* Invisible spacer to center the title */}
                    <div className="size-10" />
                  </div>
                </DialogHeader>

                <div className="flex flex-col gap-3 mt-3">
                  {formContent}
                </div>

                {/* Bottom section: pill toggles + post button */}
                <div className="flex flex-wrap items-center justify-between w-full mt-3 gap-3">
                  <div className="flex flex-wrap gap-4">
                    {/* Allow comments toggle */}
                    <button
                      type="button"
                      onClick={() => footerProps.setAllowComments(!footerProps.allowComments)}
                      className={cn(
                        "flex items-center gap-2 p-3 rounded-lg text-body-2-medium whitespace-nowrap border",
                        footerProps.allowComments
                          ? "border-transparent bg-surface-1 text-text-primary"
                          : "border-border-tertiary text-text-secondary"
                      )}
                    >
                      <Icon
                        name={footerProps.allowComments ? "check-circle" : "circle"}
                        size={18}
                      />
                      Allow comments
                    </button>

                    {/* Email toggle (hosts only, create mode) */}
                    {footerProps.isUserHost && !footerProps.isEditMode && (
                      <button
                        type="button"
                        onClick={() => !footerProps.emailQuota || footerProps.emailQuota.remaining > 0 ? footerProps.setEmailEnabled(!footerProps.emailEnabled) : undefined}
                        disabled={footerProps.emailQuota ? footerProps.emailQuota.remaining <= 0 : false}
                        className={cn(
                          "flex items-center gap-2 p-3 rounded-lg text-body-2-medium whitespace-nowrap border",
                          footerProps.emailEnabled
                            ? "border-transparent bg-surface-1 text-text-primary"
                            : "border-border-tertiary text-text-secondary"
                        )}
                      >
                        <Icon
                          name={footerProps.emailEnabled ? "check-circle" : "circle"}
                          size={18}
                        />
                        Email this post
                        {footerProps.emailQuota && (
                          <span className="text-body-3 text-text-tertiary">
                            {footerProps.emailQuota.remaining} remaining
                          </span>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Post button */}
                  <button
                    type="submit"
                    disabled={!footerProps.canSubmit}
                    className={cn(
                      "flex items-center justify-center px-4 py-3 rounded-lg text-body-2-medium whitespace-nowrap bg-olive text-off-white transition-opacity",
                      footerProps.canSubmit
                        ? "hover:opacity-90"
                        : "opacity-50 cursor-not-allowed"
                    )}
                  >
                    {footerProps.submitLabel}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </>
      )}
    </PostFormContent>
  );
}

/**
 * PostFormContent - Shared form content for creating and editing posts.
 * Uses render-children pattern to allow different wrappers (Dialog vs SidePanel).
 */
export function PostFormContent({
  clubId,
  authorId,
  editingPost,
  onClose,
  onSuccess,
  children,
}: PostFormContentProps & {
  children: (
    formContent: React.ReactNode,
    footerProps: {
      canSubmit: boolean;
      isSubmitting: boolean;
      onCancel: () => void;
      onSubmit: () => void;
      submitLabel: string;
      allowComments: boolean;
      setAllowComments: (v: boolean) => void;
      emailEnabled: boolean;
      setEmailEnabled: (v: boolean) => void;
      isUserHost: boolean;
      isEditMode: boolean;
      emailQuota: { remaining: number } | undefined;
    }
  ) => React.ReactNode;
}) {
  const isEditMode = !!editingPost;

  const [bodyText, setBodyText] = useState("");
  const [selectedMedia, setSelectedMedia] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [allowComments, setAllowComments] = useState(true);
  const [locationObject, setLocationObject] = useState<GeocodingFeature | null>(null);
  const [locationName, setLocationName] = useState("");
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [pollData, setPollData] = useState<PollData | null>(null);
  const [pollEditorOpen, setPollEditorOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Pre-upload state (like mobile) - only used in create mode
  const [preUploadState, setPreUploadState] = useState<PreUploadState>({ status: "idle" });
  const preUploadAbortRef = useRef<AbortController | null>(null);
  const mediaWasCancelledRef = useRef(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const mentionTextareaRef = useRef<import("@/components/ui/MentionTextarea").MentionTextareaHandle>(null);
  const [createPost] = useCreatePostMutation();
  const [createPoll] = useCreatePollMutation();
  const [createPostMedia] = useCreatePostMediaMutation();
  const [updatePost] = useUpdatePostMutation();
  const [sendPostEmail] = useSendPostEmailMutation();
  const { keys } = usePublicKeys();
  const mapboxKey = keys?.mapbox_public_key;

  // Content validation
  const { data: bannedWords = [] } = useFetchBannedWordsQuery();

  // Email post - fetch user role, quota, and member count
  const { data: membership } = useCheckClubMembershipQuery(
    { clubId, userId: authorId },
    { skip: !clubId || !authorId }
  );
  const isUserHost = isOwnerOrCohost(membership?.role);
  const { data: emailQuota } = useFetchClubEmailQuotaQuery(clubId, {
    skip: !isUserHost || isEditMode,
  });
  const { data: memberCount = 0 } = useFetchClubMemberCountQuery(
    { clubId },
    { skip: !isUserHost || isEditMode }
  );

  // Combined loading state for disabling interactions
  const isUploading = preUploadState.status === "uploading";
  const isLoading = isSubmitting;

  // Initialize form from editingPost when it changes
  const editingPostId = editingPost?.id;
  const editingPostBodyText = editingPost?.body_text;
  const editingPostAllowComments = editingPost?.allow_comments;
  useEffect(() => {
    if (editingPostId) {
      setBodyText(editingPostBodyText || "");
      setAllowComments(editingPostAllowComments ?? true);
    } else {
      // Reset for create mode
      setBodyText("");
      setAllowComments(true);
    }
  }, [editingPostId, editingPostBodyText, editingPostAllowComments]);

  // Existing media from editing post (read-only)
  const existingMedia = editingPost?.post_media?.[0];
  const existingMediaUrl = existingMedia?.image_medium || existingMedia?.image_full || existingMedia?.image_small;
  const existingPoll = editingPost?.poll;

  // Auto-resize textarea
  useEffect(() => {
    const textarea = mentionTextareaRef.current?.getTextarea();
    if (textarea) {
      const maxHeight = window.innerHeight * 0.75;
      textarea.style.height = "auto";
      const newHeight = Math.max(90, Math.min(textarea.scrollHeight, maxHeight));
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
    }
  }, [bodyText]);

  // Start pre-upload when media is selected (like mobile)
  const startPreUpload = useCallback(async (file: File) => {
    // Reset cancellation flag
    mediaWasCancelledRef.current = false;

    // Create abort controller for this upload
    preUploadAbortRef.current = new AbortController();

    setPreUploadState({ status: "uploading", progress: 0 });

    const isVideo = file.type.startsWith("video/");

    try {
      const onProgress = (progress: number) => {
        if (mediaWasCancelledRef.current) return;
        setPreUploadState({ status: "uploading", progress });
      };

      let upload: MediaUploadResult;
      if (isVideo) {
        const result = await uploadVideo(file, onProgress);
        upload = { kind: "video", result };
      } else {
        const result = await uploadImage(file, onProgress);
        upload = { kind: "image", result };
      }

      // Check if cancelled after upload completed
      if (mediaWasCancelledRef.current) {
        console.log("Upload completed but was cancelled, cleaning up files");
        await deleteFiles(upload.result.filenames);
        return;
      }

      setPreUploadState({ status: "complete", upload });
    } catch (error) {
      // Check if this was an intentional abort
      if (mediaWasCancelledRef.current) {
        console.log("Upload was cancelled");
        setPreUploadState({ status: "idle" });
        return;
      }

      console.error("Pre-upload failed:", error);
      setPreUploadState({
        status: "error",
        error: error instanceof Error ? error.message : "Upload failed"
      });
    }
  }, []);

  // Handle file selection - start pre-upload immediately (like mobile)
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Only one media item allowed
    if (selectedMedia) {
      return;
    }

    const file = files[0];
    setSelectedMedia(file);

    // Generate preview
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
    }
    setMediaPreview(URL.createObjectURL(file));

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Start pre-upload immediately (like mobile)
    startPreUpload(file);
  };

  // Remove image - abort upload and clean up (like mobile)
  const handleRemoveMedia = useCallback(async () => {
    // Mark as cancelled
    mediaWasCancelledRef.current = true;

    // Abort any in-progress upload
    if (preUploadAbortRef.current) {
      preUploadAbortRef.current.abort();
      preUploadAbortRef.current = null;
    }

    // If upload already completed, delete the uploaded files
    if (preUploadState.status === "complete") {
      console.log("Deleting completed upload files");
      await deleteFiles(preUploadState.upload.result.filenames);
    }

    // Clean up preview
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
    }

    // Reset state
    setSelectedMedia(null);
    setMediaPreview(null);
    setPreUploadState({ status: "idle" });
  }, [preUploadState, mediaPreview]);

  // Remove location
  const handleRemoveLocation = () => {
    setLocationObject(null);
    setLocationName("");
  };

  // Remove poll
  const handleRemovePoll = () => {
    setPollData(null);
  };

  const handleSubmit = async () => {
    if (isEditMode) {
      await handleEditSubmit();
    } else {
      await handleCreateSubmit();
    }
  };

  const handleEditSubmit = async () => {
    if (!editingPost) return;

    // Must have some content in edit mode
    const hasContent = bodyText.trim().length > 0 || !!existingMedia || !!existingPoll || !!editingPost.location_object;
    if (!hasContent) return;

    // Validate content against banned words
    const validation = validateContent(bodyText, bannedWords);
    if (!validation.isValid) {
      setValidationError("This post violates our guidelines.");
      return;
    }

    setIsSubmitting(true);
    try {
      await updatePost({
        postId: editingPost.id,
        clubId,
        updates: {
          body_text: bodyText.trim(),
          allow_comments: allowComments,
        },
      }).unwrap();

      onSuccess();
    } catch (error) {
      console.error("Failed to update post:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubmit = async () => {
    // Must have some content (text, media, location, or poll)
    if (!hasContent) return;

    // Don't submit if upload failed or still in progress
    if (preUploadState.status === "error" || preUploadState.status === "uploading") {
      console.error("Cannot submit - upload not ready");
      return;
    }

    // Validate content against banned words
    const validation = validateContent(bodyText, bannedWords);
    if (!validation.isValid) {
      setValidationError("This post violates our guidelines.");
      return;
    }

    // Validate poll options if present
    if (pollData) {
      for (const option of pollData.options) {
        const optionValidation = validateContent(option, bannedWords);
        if (!optionValidation.isValid) {
          setValidationError("This post violates our guidelines.");
          return;
        }
      }
    }

    // Show email confirmation dialog if email is enabled
    if (emailEnabled) {
      setShowEmailConfirm(true);
      return;
    }

    await doCreateSubmit();
  };

  const doCreateSubmit = async () => {
    setShowEmailConfirm(false);
    setIsSubmitting(true);

    try {
      // Get the upload result if we have a completed upload
      const mediaUpload: MediaUploadResult | null =
        preUploadState.status === "complete" ? preUploadState.upload : null;

      const isVideoUpload = mediaUpload?.kind === "video";

      // Create the post (with processing flags for video)
      const post = await createPost({
        author_id: authorId,
        club_id: clubId,
        body_text: bodyText.trim(),
        allow_comments: allowComments,
        location_name: locationName || (locationObject?.properties.name_preferred || locationObject?.properties.name) || null,
        location_object: locationObject || null,
        pinned: false,
        pinned_date: null,
        deleted_by_user: false,
        moderated_deleted: false,
        like_count: 0,
        // Video processing flags (matching mobile's PostUploadProcessor)
        ...(isVideoUpload && {
          processing_status: "processing",
          is_processing: true,
        }),
      }).unwrap();

      // Create post_media record if we uploaded media
      if (mediaUpload && post.id) {
        if (mediaUpload.kind === "image") {
          await createPostMedia({
            postId: post.id,
            clubId,
            kind: "image",
            imageSmall: mediaUpload.result.imageSmall,
            imageMedium: mediaUpload.result.imageMedium,
            imageFull: mediaUpload.result.imageFull,
            width: mediaUpload.result.width,
            height: mediaUpload.result.height,
            aspectRatio: mediaUpload.result.aspectRatio,
            sortOrder: 0,
          }).unwrap();
        } else {
          // Video: set video_url to null (Coconut webhook will set it after processing)
          // Set image_small to thumbnail for preview while processing
          await createPostMedia({
            postId: post.id,
            clubId,
            kind: "video",
            videoUrl: null,
            imageSmall: mediaUpload.result.thumbnailUrl,
            width: mediaUpload.result.width,
            height: mediaUpload.result.height,
            aspectRatio: mediaUpload.result.aspectRatio,
            duration: mediaUpload.result.duration,
            sortOrder: 0,
          }).unwrap();

          // Trigger Coconut video processing (matching mobile's PostUploadProcessor)
          await supabase.functions.invoke("process-video-upload", {
            body: {
              tempKey: mediaUpload.result.tempKey,
              postId: post.id,
              userId: authorId,
              thumbnailUrl: mediaUpload.result.thumbnailUrl,
            },
          });
        }
      }

      // If poll data exists, create the poll
      if (pollData && post.id) {
        const closesAt = new Date(
          Date.now() + pollData.durationHours * 60 * 60 * 1000
        ).toISOString();

        await createPoll({
          postId: post.id,
          durationHours: pollData.durationHours,
          closesAt,
          options: pollData.options,
          clubId,
        }).unwrap();
      }

      // Send post by email if enabled
      if (emailEnabled && post.id) {
        try {
          await sendPostEmail({ clubId, postId: post.id }).unwrap();
        } catch (emailError) {
          console.error("Failed to send post email:", emailError);
          // Don't fail the post creation if email fails
        }
      }

      // Reset form and close dialog on success
      resetForm();
      onSuccess();
    } catch (error) {
      console.error("Failed to create post:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = useCallback(() => {
    setBodyText("");
    setSelectedMedia(null);
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
    }
    setMediaPreview(null);
    setAllowComments(true);
    setLocationObject(null);
    setLocationName("");
    setPollData(null);
    setPreUploadState({ status: "idle" });
    mediaWasCancelledRef.current = false;
    setValidationError(null);
    setEmailEnabled(false);
    setShowEmailConfirm(false);
    setShowDiscardConfirm(false);
  }, [mediaPreview]);

  // Force close - clean up files and close without confirmation
  const doForceClose = useCallback(async () => {
    setShowDiscardConfirm(false);

    // If we have a completed upload that wasn't used, clean it up
    if (preUploadState.status === "complete") {
      console.log("Cleaning up unused upload on close");
      await deleteFiles(preUploadState.upload.result.filenames);
    }

    // Cancel any in-progress upload
    if (preUploadState.status === "uploading") {
      mediaWasCancelledRef.current = true;
      if (preUploadAbortRef.current) {
        preUploadAbortRef.current.abort();
      }
    }

    resetForm();
    onClose();
  }, [preUploadState, resetForm, onClose]);

  // Handle close - show confirmation if there's content to lose
  const handleClose = useCallback(async () => {
    if (isLoading || isSubmitting) return;

    // Check if form has content that would be lost
    const formHasContent = bodyText.trim().length > 0 || !!selectedMedia || !!locationObject || !!pollData;
    if (formHasContent && !isEditMode) {
      setShowDiscardConfirm(true);
      return;
    }

    await doForceClose();
  }, [isLoading, isSubmitting, bodyText, selectedMedia, locationObject, pollData, isEditMode, doForceClose]);

  // Can post if we have any content (matching mobile behavior)
  // But not while upload is still in progress
  const hasContent = bodyText.trim().length > 0 || !!selectedMedia || !!locationObject || !!pollData;
  const uploadReady = !selectedMedia || preUploadState.status === "complete";
  const canPostCreate = hasContent && !isLoading && !isSubmitting && uploadReady && preUploadState.status !== "error";

  // Edit mode: can save if there's content (including existing media/poll/location)
  const hasEditContent = bodyText.trim().length > 0 || !!existingMedia || !!existingPoll || !!editingPost?.location_object;
  const canSaveEdit = hasEditContent && !isSubmitting;

  const canSubmit = isEditMode ? canSaveEdit : canPostCreate;

  // Check if we have preview content to show (create mode only)
  const hasPreviewContent = !!selectedMedia || !!locationObject || !!pollData;

  // Get coordinates for location preview
  const locationCoords = locationObject?.geometry?.coordinates;

  // Submit label
  const submitLabel = isEditMode
    ? (isSubmitting ? "Saving..." : "Save")
    : (isSubmitting ? "Posting..." : isUploading ? "Uploading..." : "Post");

  const formContent = (
    <>
      {/* Text input with mention autocomplete - auto-grows */}
      <MentionTextarea
        ref={mentionTextareaRef}
        value={bodyText}
        onChange={(text) => {
          setBodyText(text);
          if (validationError) setValidationError(null);
        }}
        clubId={clubId}
        currentUserId={authorId}
        panelPosition="below"
        placeholder="What's happening?"
        className="w-full px-4 py-3 rounded-2xl bg-surface text-body-1 text-text-primary placeholder:text-text-tertiary resize-none focus:outline-none border border-transparent hover:border-border-tertiary focus:border-border-tertiary focus:bg-transparent"
        style={{ minHeight: 90 }}
        disabled={isLoading || isSubmitting}
        autoFocus
      />

      {/* Read-only existing media preview (edit mode) */}
      {isEditMode && existingMediaUrl && (
        <div className="rounded-2xl overflow-hidden border border-border-tertiary">
          <img
            src={existingMediaUrl}
            alt=""
            className="w-full max-h-[200px] object-cover"
            style={{
              aspectRatio:
                existingMedia?.width && existingMedia?.height
                  ? `${existingMedia.width} / ${existingMedia.height}`
                  : undefined,
            }}
          />
          <div className="px-3 py-2">
            <span className="text-body-3 text-text-tertiary">Media can't be edited</span>
          </div>
        </div>
      )}

      {/* Read-only poll indicator (edit mode) */}
      {isEditMode && existingPoll && (
        <div className="rounded-2xl border border-border-tertiary p-3 flex items-center gap-2">
          <Icon name="poll" size={16} className="text-text-tertiary" />
          <span className="text-body-3 text-text-tertiary">Poll can't be edited</span>
        </div>
      )}

      {/* Preview grid - wrapping, only shown when content exists (create mode) */}
      {!isEditMode && hasPreviewContent && (
        <div className="flex flex-wrap gap-2">
          {/* Media preview with upload progress */}
          {mediaPreview && (
            <div
              className="relative"
              style={{ height: PREVIEW_H, maxWidth: "100%" }}
            >
              {selectedMedia?.type.startsWith("video/") ? (
                <video
                  src={mediaPreview}
                  className={cn(
                    "h-full max-w-full rounded-xl object-cover transition-opacity",
                    isUploading && "opacity-70"
                  )}
                  style={{ height: PREVIEW_H }}
                  muted
                  playsInline
                />
              ) : (
                <img
                  src={mediaPreview}
                  alt="Preview"
                  className={cn(
                    "h-full max-w-full rounded-xl object-cover transition-opacity",
                    isUploading && "opacity-70"
                  )}
                  style={{ height: PREVIEW_H }}
                />
              )}
              {/* Play icon overlay for video */}
              {selectedMedia?.type.startsWith("video/") && !isUploading && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-10 h-10 bg-black/50 rounded-full flex items-center justify-center">
                    <Icon name="play" size={16} className="text-white ml-0.5" />
                  </div>
                </div>
              )}
              {/* Upload progress overlay */}
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/60 rounded-lg px-3 py-2 flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span className="text-white text-body-3">
                      {Math.round(preUploadState.progress * 100)}%
                    </span>
                  </div>
                </div>
              )}
              {/* Upload complete indicator */}
              {preUploadState.status === "complete" && (
                <div className="absolute bottom-2 left-2 bg-green-500/90 rounded-full p-1">
                  <Icon name="check" size={12} className="text-white" />
                </div>
              )}
              {/* Upload error indicator */}
              {preUploadState.status === "error" && (
                <div className="absolute bottom-2 left-2 bg-red/90 rounded-full p-1" title={preUploadState.error}>
                  <Icon name="exclamation" size={12} className="text-white" />
                </div>
              )}
              <button
                type="button"
                onClick={handleRemoveMedia}
                className="absolute top-2 right-2 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/70"
              >
                <Icon name="xmark" size={14} />
              </button>
            </div>
          )}

          {/* Location preview */}
          {locationObject && locationCoords && mapboxKey && (
            <div className="relative" style={{ height: PREVIEW_H }}>
              <button
                type="button"
                onClick={() => setLocationPickerOpen(true)}
                className="relative rounded-xl overflow-hidden border border-border-tertiary"
                style={{ width: PREVIEW_H * 0.8, height: PREVIEW_H }}
              >
                {/* Background map image */}
                <img
                  src={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-s+666(${locationCoords[0]},${locationCoords[1]})/${locationCoords[0]},${locationCoords[1]},14,0/160x200@2x?access_token=${mapboxKey}`}
                  alt="Location"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Blurred overlay with text */}
                <div className="absolute inset-0 bg-surface-1/70 backdrop-blur-[3px] flex flex-col items-center justify-center">
                  <Icon name="location-dot" size={24} className="text-text-secondary" />
                  <span className="text-body-3-medium text-text-secondary mt-2">
                    Location
                  </span>
                  <span className="text-body-3 text-text-tertiary text-center px-2 truncate max-w-full">
                    {locationObject.properties.name_preferred || locationObject.properties.name || "Selected"}
                  </span>
                </div>
              </button>
              <button
                type="button"
                onClick={handleRemoveLocation}
                className="absolute top-2 right-2 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/70 z-10"
              >
                <Icon name="xmark" size={14} />
              </button>
            </div>
          )}

          {/* Poll preview */}
          {pollData && (
            <div className="relative" style={{ height: PREVIEW_H }}>
              <button
                type="button"
                onClick={() => setPollEditorOpen(true)}
                className="rounded-xl bg-surface-1 border border-border-tertiary flex flex-col items-center justify-center hover:bg-surface-2 transition-colors"
                style={{ width: PREVIEW_H * 0.8, height: PREVIEW_H }}
              >
                <Icon name="poll" size={24} className="text-text-secondary" />
                <span className="text-body-3-medium text-text-secondary mt-2">
                  Poll
                </span>
                <span className="text-body-3 text-text-tertiary">
                  {pollData.options.length} {pollData.options.length === 1 ? "option" : "options"}
                </span>
              </button>
              <button
                type="button"
                onClick={handleRemovePoll}
                className="absolute top-2 right-2 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/70"
              >
                <Icon name="xmark" size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Action bar with small icons - below previews (create mode only) */}
      {!isEditMode && (
        <>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isLoading || isSubmitting || !!selectedMedia}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || isSubmitting || !!selectedMedia}
              className={cn(
                "p-2 rounded-full size-10 flex items-center justify-center transition-colors",
                selectedMedia
                  ? "text-text-tertiary opacity-40 cursor-not-allowed"
                  : "text-text-tertiary hover:text-text-secondary hover:bg-surface-1"
              )}
            >
              <Icon name="image" size={20} />
            </button>
            <button
              type="button"
              onClick={() => setLocationPickerOpen(true)}
              disabled={isLoading || isSubmitting || !!locationObject}
              className={cn(
                "p-2 rounded-full size-10 flex items-center justify-center transition-colors",
                locationObject
                  ? "text-text-tertiary opacity-40 cursor-not-allowed"
                  : "text-text-tertiary hover:text-text-secondary hover:bg-surface-1"
              )}
            >
              <Icon name="location-dot" size={20} />
            </button>
            <button
              type="button"
              onClick={() => setPollEditorOpen(true)}
              disabled={isLoading || isSubmitting || !!pollData}
              className={cn(
                "p-2 rounded-full size-10 flex items-center justify-center transition-colors",
                pollData
                  ? "text-text-tertiary opacity-40 cursor-not-allowed"
                  : "text-text-tertiary hover:text-text-secondary hover:bg-surface-1"
              )}
            >
              <Icon name="poll" size={20} />
            </button>
          </div>

          {/* Divider line */}
          <div className="w-full h-px bg-border-tertiary" />
        </>
      )}

      {/* Validation error message */}
      {validationError && (
        <div className="text-body-3 text-red">
          {validationError}
        </div>
      )}

      {/* Upload error message (create mode only) */}
      {!isEditMode && preUploadState.status === "error" && (
        <div className="text-body-3 text-red">
          Upload failed: {preUploadState.error}
        </div>
      )}

      {/* Email confirmation dialog */}
      {showEmailConfirm && (
        <Dialog open={showEmailConfirm} onOpenChange={setShowEmailConfirm}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Send post by email</DialogTitle>
            </DialogHeader>
            <p className="text-body-2 text-text-secondary mt-2">
              This will go to all {Math.max(0, memberCount - 1)} member{memberCount - 1 !== 1 ? "s" : ""}.
              {emailQuota && (
                <> You can email {emailQuota.remaining} more post{emailQuota.remaining !== 1 ? "s" : ""} this month.</>
              )}
            </p>
            <div className="mt-4 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowEmailConfirm(false)}
                className="px-4 py-2 rounded-full text-body-2-medium text-text-secondary hover:bg-surface-2 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doCreateSubmit}
                className="px-4 py-2 rounded-full text-body-2-medium bg-olive text-white hover:opacity-90 transition-opacity"
              >
                Send
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Discard draft confirmation dialog */}
      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogPortal>
          <AlertDialogOverlay className="backdrop-blur-none" />
          <AlertDialogPrimitive.Content
            className={cn(
              "fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2",
              "rounded-xl border border-border-tertiary bg-background p-6 shadow-lg space-y-4",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            )}
          >
            <div className="flex flex-col space-y-2">
              <AlertDialogTitle>Discard draft?</AlertDialogTitle>
              <AlertDialogDescription className="text-md">This draft will be deleted and won't be shared.</AlertDialogDescription>
            </div>
            <div className="flex gap-3 pt-2">
              <AlertDialogCancel className="flex-1 h-auto py-2 rounded-full hover:bg-transparent">
                Keep editing
              </AlertDialogCancel>
              <AlertDialogAction
                className="flex-1 h-auto py-2 rounded-full bg-text-primary text-off-white hover:bg-text-primary"
                onClick={doForceClose}
              >
                Discard
              </AlertDialogAction>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPortal>
      </AlertDialog>

      {/* Location Picker Modal (create mode only) */}
      {!isEditMode && (
        <LocationPickerModal
          open={locationPickerOpen}
          onOpenChange={setLocationPickerOpen}
          onLocationSelect={(location, customName) => {
            setLocationObject(location);
            setLocationName(customName || "");
          }}
          initialLocation={locationObject}
          initialLocationName={locationName}
          hideLocationName
        />
      )}

      {/* Poll Editor Modal (create mode only) */}
      {!isEditMode && (
        <PollEditorModal
          open={pollEditorOpen}
          onOpenChange={setPollEditorOpen}
          onSave={setPollData}
          onRemove={() => setPollData(null)}
          initialData={pollData}
        />
      )}
    </>
  );

  return children(formContent, {
    canSubmit,
    isSubmitting,
    onCancel: handleClose,
    onSubmit: handleSubmit,
    submitLabel,
    allowComments,
    setAllowComments,
    emailEnabled,
    setEmailEnabled,
    isUserHost: !!isUserHost,
    isEditMode,
    emailQuota,
  });
}
